import { Download } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useAuth } from '@/features/auth/hooks/useAuth'
import DashboardBottomSection from '@/modules/marketing/components/dashboard/DashboardBottomSection'
import DashboardStats from '@/modules/marketing/components/dashboard/DashboardStats'
import MarketingDashboardShimmer from '@/modules/marketing/components/dashboard/MarketingDashboardShimmer'
import { useMarketingDashboardStore } from '@/modules/marketing/stores/dashboard.store'
import { exportMarketingDashboardToExcel } from '@/modules/marketing/utils/exportMarketingDashboard'
import { Button } from '@/shared/components/ui/button'
import CustomDateRangePicker from '@/shared/components/ui/CustomDateRangePicker'
import DashboardPeriodFilter from '@/shared/components/ui/DashboardPeriodFilter'
import { toast } from '@/shared/lib/toast'

export function MarketingDashboardPage() {
  const { user } = useAuth()
  const firstName = user?.name?.split(' ')[0] || 'there'

  const [period, setPeriod] = useState('all')
  const [customRange, setCustomRange] = useState({ startDate: '', endDate: '' })

  const {
    campaignLeads,
    conversionRate,
    ordersClosed,
    ordersLost,
    leadsManagement,
    campaignPerformance,
    isLoading,
    hasLoaded,
    fetchDashboard,
  } = useMarketingDashboardStore()

  useEffect(() => {
    fetchDashboard(period, customRange)
  }, [fetchDashboard, period, customRange])

  const handleExport = () => {
    try {
      exportMarketingDashboardToExcel({
        campaignLeads,
        conversionRate,
        ordersClosed,
        ordersLost,
        leadsManagement,
        campaignPerformance,
        period,
        customRange,
      })
      toast.success('Dashboard exported to Excel')
    } catch (error) {
      console.error(error)
      toast.error(error?.message || 'Failed to export dashboard')
    }
  }

  if (isLoading && !hasLoaded) {
    return <MarketingDashboardShimmer />
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-5">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {period === 'custom' && (
          <CustomDateRangePicker
            startDate={customRange.startDate}
            endDate={customRange.endDate}
            onChange={setCustomRange}
          />
        )}
        <DashboardPeriodFilter value={period} onChange={setPeriod} />
        <Button
          type="button"
          variant="navy"
          size="pill"
          className="h-10 gap-1.5 px-4 text-[13px]"
          onClick={handleExport}
        >
          <Download className="size-3.5" />
          Exports
        </Button>
      </div>

      <div
        className="relative overflow-hidden rounded-2xl px-5 py-5 text-white sm:rounded-[24px] sm:px-8 sm:py-6"
        style={{
          background: `
            repeating-linear-gradient(
              -45deg,
              rgba(255,255,255,0.03) 0px,
              rgba(255,255,255,0.03) 1px,
              transparent 1px,
              transparent 14px
            ),
            linear-gradient(135deg,#0B1220 0%,#111827 55%,#1E293B 100%)
          `,
        }}
      >
        <h1 className="text-[clamp(1.5rem,5vw,2rem)] font-bold tracking-tight">
          Welcome back, {firstName}
        </h1>
      </div>

      <DashboardStats
        campaignLeads={campaignLeads}
        conversionRate={conversionRate}
        ordersClosed={ordersClosed}
        ordersLost={ordersLost}
        period={period}
        customRange={customRange}
      />

      <DashboardBottomSection
        campaignPerformance={campaignPerformance}
        leadsManagement={leadsManagement}
        period={period}
        customRange={customRange}
      />
    </div>
  )
}

export default MarketingDashboardPage
